import React, { useEffect, useState, useRef, useMemo } from "react";
import { MSpinner, MCard } from "components/Shared";
import { MCardField } from "components/Shared/MCard";
import MGridForm from "components/Shared/MGridForm";
import MGridRepeatForm, { MGridRepeatFormControlMethods } from "components/Shared/MGridRepeatForm";
import { MGridFormDefinition } from "components/Shared/MGridForm/MGridFormProps";
import { DATA_TYPE_STRING, DATA_TYPE_RADIO, DATA_TYPE_PHONE_NUMBER, DATA_TYPE_DATE_TIME_DOW, DATA_TYPE_DATE, DATA_TYPE_DROPDOWN2, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import validator from "components/Shared/MGridFormDefinitions/validator";
import { UnderwriterPermissionToClosePageInformation, AddAdditionalContactDto, Date as StupidDate } from "interfaces/Mueller.Business.Models";
import { 
  getPermissionToClosePageInfo,
  submitPermissionToClose_Ok,
  submitPermissionToClose_KeepOpen,
  submitPermissionToClose_MoreInfo,
  submitPermissionToClose_TryAgain,
  submitPermissionToClose_KeepOpenWithExtension,
  submitPermissionToClose_KeepOpenWithNote,
} from "requests/closeOut";
import { Button, FontIcon, DatePicker } from "react-md";
import moment from "moment";

export interface OtherNoteEntryForm {
  notes: string;
}

const Logo = () => {
  return (
    <div>
      <img className="logo" src="https://www2.muellerreports.com/wp-content/uploads/2016/11/Mueller-logo-web.png" alt="Mueller Logo" />
    </div>
  );
};

const emptyFormContent = {
  resolution: "",
}

interface RouteParams {
  workitemid: string;
}
interface RouteMatch {
  params: RouteParams;
}
interface Props {
  match: RouteMatch; // from router
};

const MORE_INFO = "MORE_INFO";
const TRY_AGAIN = "TRY_AGAIN";
const KEEP_OPEN = "KEEP_OPEN";
const KEEP_OPEN_WITH_EXTENSION = "KEEP_OPEN_WITH_EXTENSION";
const KEEP_OPEN_WITH_NOTE = "KEEP_OPEN_WITH_NOTE";

const UWPermissionToCloseResponse = ({
  match,
}: Props) => {
  const workitemid: string = match.params.workitemid;
  
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [info, setInfo] = useState<UnderwriterPermissionToClosePageInformation>(null);
  const [headerText, setHeaderText] = useState<string>("");
  const [isProvidingMoreInfo, setIsProvidingMoreInfo] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [moreInfoForm, setMoreInfoForm] = useState(emptyFormContent);
  const [moreInfoErrors, setMoreInfoErrors] = useState<string[]>([]);  
  const [otherNote, setOtherNote] = useState<OtherNoteEntryForm>({notes: ""});

  const mounted = useRef<boolean>(true);
  const formControlMethods = useRef<any>();
  const repeatFormControlMethods = useRef<MGridRepeatFormControlMethods>();
  const dueDateRef = useRef<Date>(null);
  const otherNotesControlMethods = useRef<any>();

  useEffect(() => {
    getPermissionToClosePageInfo(workitemid,
      (resp) => {
        setInfo(resp);

        if (resp.errorMessage === null) {
          if (resp.daysUntilClosed === 0) {
            setHeaderText(`This order will be closed today if no action is taken.`);
          }
          else {
            const closeDate = moment().add(resp.daysUntilClosed, "days").format("MM/DD/YYYY");
            setHeaderText(`This order will be closed on ${closeDate} if no action is taken.`);
          }
        }

        setIsFetching(false);
      },
      () => setIsFetching(false),
      mounted,
    )

    return (() => { mounted.current = false; });
  }, []);

  const otherNoteFormDefinition = useMemo(() => {
    let mGridFormDefinition = [
      [
        { dataType: DATA_TYPE_MULTI_LINE_STRING, dataTypeOptions: {defaultLines: 4}, label: "Notes", source: "notes", hasValidationError: validator.required }
      ],
    ];

    return mGridFormDefinition;
  }, [moreInfoForm.resolution]); 

  const handleOtherNoteFormChange = (updatedObject: OtherNoteEntryForm) => {
    setOtherNote({...updatedObject});
  };  

  const submitError = () => {
    setIsFetching(false);
    setHeaderText("We're sorry, an unknown error occurred. Please refresh the page and try again.");
  };

  const submitCloseout = () => {
    setIsFetching(true);
    setIsSubmitted(true);
    submitPermissionToClose_Ok(workitemid,
      () => {
        setIsFetching(false);
        setHeaderText("This order is now closed. Please re-order if this is needed in the future. Thank you.");
      },
      submitError,
      mounted
    )
  };

  const submitMoreInfo = () => {
    if (!formControlMethods.current.isFormValid()) return;

    // construct data for MORE_INFO + provide additional validation before submit
    const errors = [];
    let adds: AddAdditionalContactDto[] = [];
    if (moreInfoForm.resolution === MORE_INFO) {
      // validate more info form
      if (!repeatFormControlMethods.current.validate()) return;

      adds = repeatFormControlMethods.current.getData().map(data => {
        return {
          role: data.role,
          phoneNumber: data.phoneNumber,
          emailAddress: data.emailAddress,
          contactInstructions: data.contactInstructions,
        }
      });

      // validate to ensure we don't already have this number on the case      
      for (const add of adds) {
        if (add.phoneNumber && info.existingPhoneNumbers.includes(add.phoneNumber.replace(/\D/g, ""))) {
          errors.push(`Phone number ${add.phoneNumber} has already been attempted.`);
        }
      }      
    }
    else if (moreInfoForm.resolution === KEEP_OPEN_WITH_EXTENSION && dueDateRef.current === null) {
      errors.push("Must select a new due date");
    }
    else if (moreInfoForm.resolution === KEEP_OPEN_WITH_NOTE) {
      if (!otherNotesControlMethods.current?.isFormValid()) {
        errors.push("Please provide notes");
      }
    }

    if (errors.length) {
      setMoreInfoErrors(errors);
      return;
    }

    // submit
    setIsFetching(true);
    setIsSubmitted(true);
    if (moreInfoForm.resolution === MORE_INFO) {
      submitPermissionToClose_MoreInfo(workitemid, adds,
        () => {
          // add the additional phone numbers to the page so they are listed after the submit
          info.existingPhoneNumbers = info.existingPhoneNumbers.concat(adds.filter(a => a.phoneNumber).map(v => v.phoneNumber));
          setInfo(info);

          setIsFetching(false);
          setHeaderText("Attempts to schedule will be resumed. Thank you for providing more information.");
        },
        submitError,
        mounted
      );
    }
    if (moreInfoForm.resolution === TRY_AGAIN) {      
      submitPermissionToClose_TryAgain(workitemid,
        () => {
          setIsFetching(false);
          const headerText = info.holdCloseoutUntilDue ? 
            "Thank you for your response. If we do not receive a response from the appropriate party by the due date, this order will be closed." :
            "Thank you for your response. If we do not receive a response from the appropriate party in the next 7 days, this order will be closed.";
          setHeaderText(headerText);
        },
        submitError,
        mounted
      );
    }
    if (moreInfoForm.resolution === KEEP_OPEN) {
      submitPermissionToClose_KeepOpen(workitemid,
        () => {
          setIsFetching(false);
          const headerText = info.holdCloseoutUntilDue ? 
            "Thank you for your response. This order will be closed on the due date if no additional action is taken." :
            "Thank you for your response. This order will be closed in 7 days if no additional action is taken.";
          setHeaderText(headerText);
        },
        submitError,
        mounted
      );
    }
    if (moreInfoForm.resolution === KEEP_OPEN_WITH_EXTENSION) {
      const year = dueDateRef.current.getFullYear();
      const month = dueDateRef.current.getMonth() + 1;
      const day = dueDateRef.current.getDate();
      submitPermissionToClose_KeepOpenWithExtension(workitemid,
        { extendedDueDate: dueDateRef.current as unknown as StupidDate }, // this is fun, there is a date type in typescriptgenerator that conflicts with native JS date
        () => {
          // set the new due date to the info so is is correct after the submit
          info.dueDate = dueDateRef.current as unknown as StupidDate;
          setInfo(info);

          setIsFetching(false);
          setHeaderText(`Your due date extension request has been processed. Your new due date is ${month}/${day}/${year}. If we do not receive a response from the appropriate party to schedule the inspection by the due date, this order will be closed.`);
        },
        submitError,
        mounted
      )
    }
    if (moreInfoForm.resolution === KEEP_OPEN_WITH_NOTE) {
      submitPermissionToClose_KeepOpenWithNote(workitemid, otherNote.notes,
        () => {
          setIsFetching(false);
          const headerText = info.holdCloseoutUntilDue ? 
            "Thank you for your response. This order will be closed on the due date if no additional action is taken." :
            "Thank you for your response. This order will be closed in 7 days if no additional action is taken.";          
          setHeaderText(headerText);
        },
        submitError,
        mounted
      );
    }    
  };

  const handleMoreInfoFormChange = (updatedObject) => setMoreInfoForm({ ...updatedObject });

  const errorPage = () => {
    return (
      <div className="uw-permission-response">
        <Logo />
        <div>
          <h2>{info.errorMessage}</h2>
        </div>
      </div>
    );
  };

  const phoneOrEmailRequired = (data, field, value) => {
    const phone = (data.phoneNumber ?? "").trim();
    const email = (data.emailAddress ?? "").trim();
    if (!phone && !email) return "Additional Contact requires a Phone # or an Email Address.";
  };

  const interactivePageContent = () => {

    let options = [
      { label: "I would like to provide alternate contact information - please try to reach out again to schedule.", value: MORE_INFO },
    ];

    if (!info.didAlreadySelectTryAgain && !info.didAlreadySelectKeepOpenWithExtension) {
      const optionText = info.holdCloseoutUntilDue ? 
        "I have contacted the policyholder, agent, or appropriate party - they will be reaching out to you to schedule the inspection. If you do not receive a response before the due date, please close this order out." :
        "I have contacted the policyholder, agent, or appropriate party - they will be reaching out to you to schedule the inspection. If you do not receive a response in the next 7 days, please close this order out.";
      
      options.push({ label: optionText, value: TRY_AGAIN });
    }
      

    if (!info.didAlreadySelectKeepOpen && !info.didAlreadySelectKeepOpenWithExtension && !info.didAlreadySelectTryAgain) {
      const optionText = info.holdCloseoutUntilDue ? 
        "I am contacting the policyholder, agent, or appropriate party to recommend they reach out to schedule the inspection - please keep this order on hold until the due date." :
        "I am contacting the policyholder, agent, or appropriate party to recommend they reach out to schedule the inspection - please keep this order on hold for 7 days.";

      options.push({ label: optionText, value: KEEP_OPEN });
    }

    if (info.maximumDueDateExtensionDays > 0 && !info.didAlreadySelectKeepOpenWithExtension) {
      options.push({
        label: "I am contacting the policyholder, agent, or appropriate party to recommend they reach out to schedule the inspection - I would like to extend the due date of this order to allow more time to schedule.",
        value: KEEP_OPEN_WITH_EXTENSION
      })
    }

    const optionText = info.holdCloseoutUntilDue ? 
      "Other - I would like to provide more detail. Please keep this order on hold until the due date." :
      "Other - I would like to provide more detail. Please keep this order open for another 7 days.";    
    options.push({ label: optionText, value: KEEP_OPEN_WITH_NOTE });
    
    const moreInfoFormDefinition: MGridFormDefinition[][] = [
      [
        { 
          dataType: DATA_TYPE_RADIO, 
          label: "Select one:", 
          source: "resolution",
          hasValidationError: validator.required,
          dataTypeOptions: { 
            options: options
          }
        }
      ],
    ];

    const additionalContactFormDefinition: MGridFormDefinition[][] = [
      [
        { 
          dataType: DATA_TYPE_DROPDOWN2, 
          label: "Role",
          source: "role",
          hasValidationError: validator.required,
          dataTypeOptions: {
            placeholder: "--SELECT--",
            options: [
              { label: "Insured", value: "Insured"},
              { label: "Tenant", value: "Tenant"},
              { label: "Manager", value: "Manager"},
              { label: "Property Manager", value: "Property Manager"},
              { label: "Agent", value: "Agent"},
              { label: "Other", value: "Other"},
            ]
          }
        }
      ],
      [
        { dataType: DATA_TYPE_STRING, label: "Phone #", source: "phoneNumber", hasValidationError: validator.combineValidators(phoneOrEmailRequired, validator.phoneNumber) }
      ],
      [
        { dataType: DATA_TYPE_STRING, label: "Email Address", source: "emailAddress", hasValidationError: validator.combineValidators(phoneOrEmailRequired, validator.emailAddress) }
      ],
      [
        { dataType: DATA_TYPE_STRING, label: "Contact Instructions", source: "contactInstructions" }
      ]
    ];

    const dueDateMoment = moment(info.dueDate).startOf("day");
    const todayMoment = moment().startOf("day");
    const effectiveMinDate = dueDateMoment.isAfter(todayMoment) ? dueDateMoment : todayMoment;

    return (
      <>
        {info.specialAttentionNotes &&
        <MCard
          title="Special Attention Notes"
        >
          {info.specialAttentionNotes}
        </MCard>
        }
        <MCard
          title="Call Log"
        >
          {info.callLogs.map((c, i) => (
            <MCard
              title={`Call #${i + 1}`}
              allowMultipleColumns={false}
              fields={[
                { key: `Date${i}`, label: "Date", value: c.dateCreated, dataType: DATA_TYPE_DATE_TIME_DOW},
                { key: `Contact${i}`, label: "Contact", value: c.contact},
                { key: `Details${i}`, label: "Details", value: c.details},
                { key: `Notes${i}`, label: "Notes", value: c.notes},
              ]}
            />
          ))}
        </MCard>
        {!isProvidingMoreInfo &&
          <div className="button-container">
            <Button
              className="button"
              onClick={submitCloseout}
            >
            Close Out
            </Button>
            <Button
              className="button"
              onClick={() => setIsProvidingMoreInfo(true)}
            >
            Provide More Info
            </Button>
        </div>
        }
        {isProvidingMoreInfo &&
          <>
            <div className="more-info">
              <MGridForm
                key="submit-more-info"
                data={moreInfoForm}
                onChange={handleMoreInfoFormChange}
                formDefinition={moreInfoFormDefinition}
                getControlMethods={cm => { formControlMethods.current = cm; }}
                webform
              />
              {moreInfoForm.resolution === MORE_INFO &&
                <MGridRepeatForm
                  initialData={[{}]}
                  formDefinition={additionalContactFormDefinition}
                  getControlMethods={cm => { repeatFormControlMethods.current = cm; }}
                  title="Additional Contact"
                  minInstances={1}
                  maxInstances={4}
                  addButtonText="+ Add Another Contact"
                />
              }
              {moreInfoForm.resolution === KEEP_OPEN_WITH_EXTENSION &&
                <DatePicker
                  id="uw-perm2close-duedate"
                  className="date-picker"
                  label="New Due Date"
                  onChange={(dateString, dateObj) => {dueDateRef.current = dateObj;}}
                  minDate={effectiveMinDate.clone().add(1, "days").toDate()}
                  maxDate={effectiveMinDate.clone().add(info.maximumDueDateExtensionDays, "days").toDate()}
                />
              }
              {moreInfoForm.resolution === KEEP_OPEN_WITH_NOTE &&
                <MGridForm
                  width={750}
                  data={otherNote}
                  onChange={handleOtherNoteFormChange}
                  formDefinition={otherNoteFormDefinition}
                  portalTarget={() => document.querySelector("more-info")}
                  getControlMethods={cm => { otherNotesControlMethods.current = cm; }}
                  webform
                />   
              }              
            </div>
            <div className="button-container">
              <Button
                className="button"
                onClick={() => setIsProvidingMoreInfo(false)}
              >
              Back
              </Button>
              <Button
                className="button"
                onClick={submitMoreInfo}
              >
              Submit
              </Button>
          </div>
          <ul className="more-info-errors">
            {moreInfoErrors.map((v) => <li key={v}>{v}</li>)}
          </ul>
        </>
        }
      </>
    )
  };

  if (isFetching) return (<MSpinner />);
  if (info.errorMessage !== null) return errorPage();

  const ContactUs = () => {

    const href = `tel:+${info.muellerPhoneNumber.replace(/\D/g, "")}`;
    return (
      <MCard
        title="Contact us"
      >
        <p className="contact-us-row"><FontIcon>access_time</FontIcon><span>Monday - Friday 8am - 8pm EST</span></p>
        <p className="contact-us-row"><FontIcon>smartphone</FontIcon><a href={href}>{info.muellerPhoneNumber}</a></p>
      </MCard>
    );
  };

  const getOrderInformationFields = (): MCardField[] => {
    const fields = [];
    const addField = (key, label, value, dataType = undefined) => {
      if (value) {
        const field: MCardField = {
          key: key,
          label: label,
          value: value
        };
        if (dataType) {
          field.dataType = dataType;
        }
        fields.push(field);
      }
    };
    
    addField("control-number", "Control #", info.controlIdNumber);
    addField("policy-number", "Policy #", info.policyNumber);
    addField("customer", "Customer", info.customerInfo);
    addField("report-type", "Report Type", info.reportType);
    addField("survey-address", "Survey Address", info.surveyAddress.displayAddress);
    addField("due-date", "Due Date", info.dueDate, DATA_TYPE_DATE);
    addField("ph-name", "Policyholder Name", info.policyholderName.fullName);
    addField("ph-email", "Policyholder Email", info.email);
    addField("agent-name", "Agent Name", info.agentName);
    addField("agent-code", "Agent Code", info.agentCode);
    addField("agent-phone", "Agent Phone", info.agentPhone);
    addField("agent-email", "Agent Email", info.agentEmail);

    const agentPhoneDigitsOnly = info.agentPhone ? info.agentPhone.replace(/\D/g, "") : "";
    for (let i = 0; i < info.existingPhoneNumbers.length; i++) {
      const phoneNum = info.existingPhoneNumbers[i];
      if (phoneNum === agentPhoneDigitsOnly) continue;

      addField(`phone${i}`, `Phone #${i + 1}`, phoneNum, DATA_TYPE_PHONE_NUMBER);
    }

    return fields;
  };

  return (
    <div className="uw-permission-response">
      <Logo />
      <MCard
        title="Status">
        <h2>{headerText}</h2>
      </MCard>      
      <MCard
        title="Order Information"
        fields={getOrderInformationFields()}
        allowMultipleColumns={false}
      />
      {!isSubmitted && interactivePageContent()}
      {(isProvidingMoreInfo || isSubmitted) && <ContactUs />}
    </div>
  );
};

export default UWPermissionToCloseResponse;